import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, Navigate } from "react-router";
import { useAppSelector } from "../../state/store";
import { Row } from "../../components/Row";
import { VCStatsDisplay } from "./VCStatsDisplay";
import { Card } from "../../components/Card/Card";
import { Ruling } from "../../components/Ruling";
import { DoubleClickable } from "../../components/DoubleClickable";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import { blacklistedWords } from "../../util/DANGERblacklistedWords";
import { swipeCutoff } from "../../util/swiping";
import { Swiping } from "../../types/generalTypes";
import { getTimeDisplay } from "../../util/timeUtil";
import QRGenerator from "qrcode-generator";
import { RequestHandlerReturn } from "../../hooks/requestHandler";
import { VerseType } from "../../types/practiceTypes";
import {
  TotalVerseCardsStats,
  verseCardsStateKey,
  VerseStats,
} from "./VerseCardsData";
import dayjs from "dayjs";
import { shuffle } from "../../util/generalUtil";
import { Modal } from "../../components/Modal/Modal";
import { Grid } from "@mui/material";

type VerseCardsPhaseType = "READY" | "ANSWERING" | "RULING" | "DONE";
interface WordType {
  word: string;
  status: "" | "C" | "I";
}

interface VerseCardsGameProps {
  material: VerseType[]; // The main, possibly randomized, verses
  bottomOfDeck?: VerseType[]; // Verses already missed and at the bottom of the deck
  startingStats: TotalVerseCardsStats; // Stats to display
  startingVerseStats: VerseStats[]; // Stats for individual verses
  maxTime?: number; // Maximum amount of time to allow per verse
  onComplete: (stats: TotalVerseCardsStats, fullStats: VerseStats[]) => void; // To be called when all verses are complete
  runId: string; // An ID for the run
  allowExport: boolean;
  allowSkip?: boolean;
  handler: RequestHandlerReturn;
  mode: "QUIZZER" | "COACH";
}

/**
 * Game for verse cards, to be used on any other page.
 * This returns a page, so the parent component should not return any other parent elements.
 */
export function VerseCardsGame(props: VerseCardsGameProps) {
  const navigate = useNavigate();
  const { transcript, resetTranscript } = useSpeechRecognition();
  const settings = useAppSelector((state) => state.settings);
  const chapters = settings.selectedChapters;

  const randomized = useRef(props.material);
  const bottomOfDeck = useRef<VerseType[]>(props.bottomOfDeck || []);
  const [previousVerse, setPreviousVerse] = useState<
    { verse: VerseType; ruling: "DECK" | "FLY" } | undefined
  >();
  const [words, setWords] = useState<WordType[]>(
    props.material[0].verse.split(" ").map((w) => ({ word: w, status: "" }))
  );
  const displayWords = useMemo(() => words.filter((w) => !!w.status), [words]);
  const [phase, setPhase] = useState<VerseCardsPhaseType>("READY");
  const phaseRef = useRef<VerseCardsPhaseType>(phase);
  useEffect(() => {
    if (phase === "DONE") {
      props.onComplete(stats.current, verseStats.current);
    }
  }, [phase]);
  const [showQRExport, setShowQRExport] = useState(false);
  const [skipOpen, setSkipOpen] = useState(false);
  const closeSkip = () => setSkipOpen(false);

  useEffect(() => {
    if (showQRExport) {
      try {
        // Assemble data to put into the qr
        const content = {
          s: stats.current, // stats
          v: [] as string[], // remaining verses
          d: [] as string[],
        };
        randomized.current.forEach((r) => {
          content.v.push(`${r.book}-${r.chapter}-${r.verseNum}`);
        });
        bottomOfDeck.current.forEach((d) => {
          content.d.push(`${d.book}-${d.chapter}-${d.verseNum}`);
        });
        const qr = QRGenerator(0, "H"); // high tolerance for error correction
        qr.addData(JSON.stringify(content));
        qr.make();
        // Add resulting image to the div
        const el = document.getElementById("qr-code");
        if (el) {
          el.innerHTML = qr.createImgTag().replace(/210/g, "350");
        }
      } catch {
        // Any error, but probably too much data
        props.handler.showMessage(
          "There are too many verses remaining to export a QR code.",
          "error"
        );
      }
    }
  }, [showQRExport]);

  // Most probable ruling, which could be nothing
  const prioritizeRuling = useMemo(() => {
    let ruling = 0 as number | undefined;
    if (phase !== "RULING") return ruling;
    words.forEach((w) => {
      if (w.status === "I") ruling = 1;
      if (w.status === "" && ruling === 0) ruling = undefined;
    });
    return ruling;
  }, [words, phase]);

  const swipeStatusDefault: Swiping = {
    sX: -1,
    sY: -1,
    eX: -1,
    eY: -1,
  };
  const [swipeStatus, setSwipeStatus] = useState<Swiping>(swipeStatusDefault);

  const stats = useRef(props.startingStats);

  const verseStats = useRef<VerseStats[]>(props.startingVerseStats);

  const timeStart = useRef(0);
  const [timeTaken, setTimeTaken] = useState(0);
  useEffect(() => {
    timeTakenRef.current = timeTaken;
  }, [timeTaken]);
  const timeTakenRef = useRef(0);
  const [timeStarted, setTimeStarted] = useState(false); // Only for coach mode

  /**
   *
   * @param verse the verse to show
   * @param compare words to compare to
   * @returns the first parameter, with words in red that the second string doesn't have
   */
  const getStyledVerse = (verse: string, compare: string) => {
    if (!compare) return verse;
    const words = verse.split(" ");
    const compareWords = compare.split(" ");
    return (
      <span>
        {words.map((word, index) => {
          blacklistedWords.forEach((blacklisted) => {
            if (word.includes(blacklisted)) {
              const censored =
                blacklisted.substring(0, 1) +
                "*".repeat(blacklisted.length - 1);
              word = word.replace(blacklisted, censored);
            }
          });

          let included = false;
          compareWords.forEach((word2) => {
            if (
              word.replace(/[^a-zA-Z0-9]+/g, "").toLowerCase() ===
              word2.replace(/[^a-zA-Z0-9]+/g, "").toLowerCase()
            )
              included = true;
          });
          return (
            <span key={index} style={{ color: included ? undefined : "red" }}>
              {word + " "}
            </span>
          );
        })}
      </span>
    );
  };

  const clickNextVerse = () => {
    setPhase(props.mode === "COACH" ? "RULING" : "ANSWERING");
    setShowQRExport(false);
    phaseRef.current = "ANSWERING";
    timeStart.current = Date.now();
    setTimeStarted(false);
    setTimeTaken(0);
    resetTranscript();
    if (settings.vcVoiceToText)
      SpeechRecognition.startListening({ continuous: true });
  };
  const clickShowAnswer = () => {
    setPhase("RULING");
    phaseRef.current = "RULING";
    window.scrollTo(0, 0);
    setTimeTaken((Date.now() - timeStart.current) / 1000);
    SpeechRecognition.stopListening();
  };
  const makeRuling = (theRuling?: number) => {
    let timeAmount = timeTakenRef.current;
    if (timeAmount === 0 && props.mode === "COACH") {
      timeAmount = (Date.now() - timeStart.current) / 1000;
      if (theRuling !== 2) setTimeTaken(timeAmount);
    }
    let newRandomized = [...randomized.current];
    let newDeck = [...bottomOfDeck.current];
    let backInDeck = false;
    setWords((words) => {
      const currentVerse = newRandomized[0];
      if (!currentVerse) return words;
      const ruling =
        theRuling !== undefined
          ? theRuling
          : words.some((w) => w.status === "I")
            ? 1
            : 0;
      if (ruling === 2 || (ruling === 1 && settings.vcReview)) {
        if (ruling === 2) {
          newRandomized.push(currentVerse);
        } else {
          newDeck.push(currentVerse);
        }
        backInDeck = true;
      }
      const isReview =
        stats.current.totalVerses <=
        stats.current.correct + stats.current.incorrect;
      if (ruling === 0) {
        stats.current.correct += 1;
        if (!isReview) stats.current.absCorrect += 1;
      }
      if (ruling === 1) {
        stats.current.incorrect += 1;
        if (!isReview) stats.current.absIncorrect += 1;
      }
      if (ruling !== 2) {
        stats.current.time = stats.current.time + timeAmount;
        if (!isReview)
          stats.current.absTime = stats.current.absTime + timeAmount;
        const verseIndex = verseStats.current.findIndex(
          (v) =>
            v.b === currentVerse.book &&
            v.c === currentVerse.chapter &&
            v.v === currentVerse.verseNum
        );
        const verseItem: VerseStats =
          verseIndex >= 0
            ? { ...verseStats.current[verseIndex] }
            : {
                v: currentVerse.verseNum,
                b: currentVerse.book,
                c: currentVerse.chapter,
                incorrect: 0,
                correct: 0,
                time: 0,
              };
        verseItem.time += timeAmount;
        if (ruling === 1) verseItem.incorrect += 1;
        else verseItem.correct += 1;
        if (verseIndex === -1) {
          verseStats.current.push(verseItem);
        } else {
          verseStats.current[verseIndex] = verseItem;
        }
      }

      // Remove current verse
      setPreviousVerse({
        verse: newRandomized[0],
        ruling: backInDeck ? "DECK" : "FLY",
      });
      newRandomized.splice(0, 1);

      // Store progress, even if the run is finished
      const currentState = JSON.stringify({
        versesRemaining: newRandomized,
        deck: newDeck,
        stats: stats.current,
        verseStats: verseStats.current,
        runId: props.runId,
        time: dayjs().format(),
      });
      localStorage.setItem(verseCardsStateKey, currentState);

      if (newRandomized.length === 0) {
        newRandomized = newDeck;
        newDeck = [];
        if (settings.vcRandomize) {
          shuffle(newRandomized);
        }
      }

      // Move to either done or ready
      const newPhase = newRandomized.length === 0 ? "DONE" : "READY";
      setPhase(newPhase);
      phaseRef.current = newPhase;

      if (newRandomized.length === 0) {
        return [];
      } else {
        randomized.current = newRandomized;
        bottomOfDeck.current = newDeck;
        return newRandomized[0].verse
          .split(" ")
          .map((w) => ({ word: w, status: "" }));
      }
    });
  };
  const upHandler = (e: KeyboardEvent) => {
    if (props.mode === "COACH") return;
    if (e.code === "Space") {
      switch (phaseRef.current) {
        case "READY":
          clickNextVerse();
          break;
        case "ANSWERING":
          clickShowAnswer();
          break;
        case "RULING":
          makeRuling();
          break;
        default:
          break;
      }
      return;
    }
    if (phaseRef.current === "ANSWERING") {
      // Check if this is the correct first character
      const keyPressed = e.code.substring(3, 4).toLowerCase();
      if (keyPressed.match(/[a-z0-9]/i)) {
        setWords((words) => {
          const newWords = [...words];
          const index = newWords.findIndex((w) => w.status === "");
          const firstLetter = newWords[index].word
            .toLowerCase()
            .match(/[a-z0-9]/)
            ?.pop();
          newWords[index] = {
            ...newWords[index],
            status: firstLetter && firstLetter === keyPressed ? "C" : "I",
          };
          if (index + 1 === newWords.length) clickShowAnswer();
          return newWords;
        });
      }
    }
  };
  useEffect(() => {
    window.addEventListener("keyup", upHandler);

    // Remove event listeners on cleanup
    return () => {
      window.removeEventListener("keyup", upHandler);
      SpeechRecognition.stopListening();
    };
  }, []);

  if (chapters.length === 0) {
    return <Navigate to="/" />;
  }

  const cardSize = {
    height: "min(60vh, 280px)",
    width: "min(100%, 500px)",
  };
  return (
    <DoubleClickable
      style={{
        touchAction: phase === "RULING" ? "none" : "auto",
        minHeight: "calc(100vh - 70px)",
        minWidth: 300,
      }}
      doubleClick={() => {
        if (settings.touchscreen) {
          if (phase === "READY" && randomized.current.length > 0) {
            clickNextVerse();
          }
          if (phase === "ANSWERING") {
            clickShowAnswer();
          }
        }
      }}
      onTouchStart={(e) => {
        if (phase !== "RULING") return;
        setSwipeStatus({
          ...swipeStatus,
          sX: e.touches[0].clientX,
          eX: e.touches[0].clientX,
          sY: e.touches[0].clientY,
          eY: e.touches[0].clientY,
        });
      }}
      onTouchMove={(e) => {
        if (swipeStatus.sX < 0 && swipeStatus.sY < 0) return;
        setSwipeStatus({
          ...swipeStatus,
          eX: e.touches[0].clientX,
          eY: e.touches[0].clientY,
        });
      }}
      onTouchEnd={() => {
        if (settings.touchscreen && phase === "RULING") {
          if (Math.abs(swipeStatus.eX - swipeStatus.sX) > swipeCutoff) {
            makeRuling(swipeStatus.eX > swipeStatus.sX ? 0 : 1);
          }
        }
        setSwipeStatus(swipeStatusDefault);
      }}
    >
      <Card
        key={phase === "READY" ? "next-verse" : randomized.current[0].reference}
        data-testId="vc-main-card"
        size={cardSize}
        cardsUnder={Math.min(
          randomized.current.length + bottomOfDeck.current.length - 1,
          3
        )}
        frontContent={
          <>
            <span style={{ display: "block", fontSize: 18 }}>
              {phase === "READY"
                ? "Next Verse"
                : randomized.current[0].reference}
            </span>
            {(phase === "ANSWERING" || phase === "RULING") &&
              displayWords.length > 0 && (
                <span style={{ display: "block", marginTop: 15, fontSize: 16 }}>
                  {displayWords.map((w, i) => (
                    <span
                      key={i}
                      style={{
                        color: w.status === "C" ? undefined : "red",
                      }}
                    >
                      {w.word + " "}
                    </span>
                  ))}
                </span>
              )}
          </>
        }
        isFlipped={phase === "RULING"}
        ariaLabel={
          phase === "READY"
            ? "Next Verse"
            : phase === "ANSWERING"
              ? `Show Answer for ${randomized.current[0].reference}`
              : randomized.current[0].verse
        }
        backContent={
          <>
            <span style={{ display: "block", fontSize: 18 }}>
              {randomized.current[0].reference}
            </span>
            <span style={{ display: "block", marginTop: 10, fontSize: 16 }}>
              {getStyledVerse(randomized.current[0].verse, transcript)}
            </span>
            {transcript && (
              <span style={{ display: "block", marginTop: 10, fontSize: 16 }}>
                {getStyledVerse(transcript, randomized.current[0].verse)}
              </span>
            )}
          </>
        }
        onClick={() => {
          if (phase === "READY") {
            clickNextVerse();
          } else if (phase === "ANSWERING") {
            clickShowAnswer();
          }
        }}
        allowSwipe
        oldContent={
          phase === "ANSWERING" ||
          (phase === "RULING" && props.mode === "COACH")
            ? {
                content: <span style={{ fontSize: 18 }}>Next Verse</span>,
                movement: "FLY",
                direction: "left",
              }
            : phase === "READY" && previousVerse
              ? {
                  content: (
                    <>
                      <span style={{ display: "block", fontSize: 16 }}>
                        {previousVerse.verse.reference}
                      </span>
                      <span style={{ display: "block", marginTop: 10 }}>
                        {previousVerse.verse.verse}
                      </span>
                    </>
                  ),
                  movement: previousVerse.ruling,
                  direction:
                    previousVerse.ruling === "FLY" ? "right" : undefined,
                }
              : undefined
        }
        disableAnimation={settings.vcDisableAnimation}
      />
      {phase === "RULING" && settings.touchscreen && swipeStatus.sX >= 0 && (
        <div
          style={{
            backgroundColor: swipeStatus.sX < swipeStatus.eX ? "green" : "red",
            height: 5,
            position: "absolute",
            left: Math.min(swipeStatus.sX, swipeStatus.eX),
            top: swipeStatus.sY - 5,
            width: Math.abs(swipeStatus.sX - swipeStatus.eX),
            zIndex: 7,
          }}
        />
      )}
      <div style={{ marginTop: 20 }}>
        {phase === "ANSWERING" && stats.current.correct === 0 && (
          <p
            className="important-information"
            style={{ marginTop: 20 }}
          >{`Quote the verse, and click Show Answer when you are finished. ${
            settings.vcVoiceToText
              ? "The words you speak will show up on the screen, compared to the actual verse. "
              : ""
          }If you have a keyboard, you can also type the first letter of each word of the verse.`}</p>
        )}
        {phase === "RULING" && (
          <>
            {settings.touchscreen ? (
              <div>
                <p className="important-information">
                  Swipe right for 20 Points, left for Error.
                </p>
                <button
                  className="link"
                  onClick={() => makeRuling(2)}
                  style={{ paddingLeft: 0 }}
                >
                  Throwout
                </button>
              </div>
            ) : (
              <div style={{ marginTop: 20 }}>
                <Ruling
                  onClick={(type) => makeRuling(type)}
                  prioritize={prioritizeRuling}
                />
              </div>
            )}
            {props.mode === "COACH" && (
              <Row style={{ marginTop: 20, width: 450, position: "relative" }}>
                <button
                  className="secondary-btn"
                  style={{
                    visibility:
                      timeStarted || timeTaken > 0 ? "hidden" : "visible",
                  }}
                  onClick={() => {
                    setTimeStarted(true);
                    timeStart.current = Date.now();
                  }}
                >
                  Start Time
                </button>
                <button
                  className="secondary-btn"
                  style={{
                    visibility: timeTaken > 0 ? "hidden" : "visible",
                    position: "absolute",
                    right: 0,
                  }}
                  onClick={() => {
                    setTimeTaken((Date.now() - timeStart.current) / 1000);
                  }}
                >
                  End Time
                </button>
              </Row>
            )}
          </>
        )}
      </div>
      {phase === "READY" && (
        <VCStatsDisplay
          stats={stats.current}
          remaining={randomized.current.length + bottomOfDeck.current.length}
          timeTaken={timeTaken}
        />
      )}
      {phase === "RULING" && (
        <div style={{ marginTop: 10 }}>
          <div
            style={{
              visibility:
                timeTaken === 0 && props.mode === "COACH"
                  ? "hidden"
                  : "visible",
            }}
          >{`Time This Verse: ${getTimeDisplay(timeTaken)}`}</div>
          <div style={{ fontSize: 12, marginTop: 10 }}>
            {randomized.current[0]?.questions?.map((question, index) => (
              <div key={index}>{question}</div>
            ))}
          </div>
        </div>
      )}
      {phase === "READY" && (
        <>
          <Row style={{ marginTop: 20 }}>
            <button
              className="secondary-btn"
              onClick={() => {
                SpeechRecognition.stopListening();
                navigate("/");
              }}
            >
              Go Back
            </button>
            {props.allowExport &&
              !showQRExport &&
              randomized.current.length < 150 && (
                <button
                  className="link"
                  onClick={() => setShowQRExport(true)}
                  style={{ marginLeft: 10 }}
                >
                  Export QR Code
                </button>
              )}
          </Row>
          {bottomOfDeck.current.length > 0 &&
            props.allowSkip &&
            stats.current.totalVerses >
              stats.current.correct + stats.current.incorrect && (
              <>
                <br />
                <button className="link" onClick={() => setSkipOpen(true)}>
                  Skip to End
                </button>
              </>
            )}
        </>
      )}
      {showQRExport && (
        <div style={{ marginTop: 20 }}>
          <p>
            You can export your progress to continue later. You can screenshot
            the code or scan it from another device.
          </p>
          <div id="qr-code" style={{ marginTop: 40 }} />
        </div>
      )}
      <Modal open={skipOpen} onClose={closeSkip} title="Skip to End">
        <Grid container spacing={2}>
          <Grid size={6}>
            <button
              className="clickable"
              onClick={() => {
                const newRandomized = [
                  ...shuffle(bottomOfDeck.current),
                  ...randomized.current,
                ];
                bottomOfDeck.current = [];
                randomized.current = newRandomized;
                setWords(
                  newRandomized[0].verse
                    .split(" ")
                    .map((w) => ({ word: w, status: "" }))
                );
                closeSkip();
              }}
            >
              Review Missed
            </button>
            <p>
              Review the verses you've missed, then resume the rest of the deck.
            </p>
          </Grid>
          <Grid size={6}>
            <button
              className="clickable"
              onClick={() => {
                const versesRemoved = randomized.current.length;
                const newRandomized = [...shuffle(bottomOfDeck.current)];
                bottomOfDeck.current = [];
                randomized.current = newRandomized;
                stats.current = {
                  ...stats.current,
                  totalVerses: stats.current.totalVerses - versesRemoved,
                };
                setWords(
                  newRandomized[0].verse
                    .split(" ")
                    .map((w) => ({ word: w, status: "" }))
                );
                closeSkip();
              }}
            >
              Skip All Remaining
            </button>
            <p>Review the verses you've missed and skip all other verses.</p>
          </Grid>
        </Grid>
        <button
          className="secondary-btn"
          onClick={closeSkip}
          style={{ marginTop: 20 }}
        >
          Cancel
        </button>
      </Modal>
    </DoubleClickable>
  );
}
